import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { secondsToHours } from 'date-fns';
import { useCallback } from 'react';

import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useDeviceFingerprintContext } from '#src/contexts/deviceFingerprint/index.ts';
import { createResendEmailConfirmationButton } from '#src/contexts/restrictedAccess/ResendEmailConfirmationLink/index.tsx';
import { useRestrictedAccessContext } from '#src/contexts/restrictedAccess/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { getClientId } from '#src/lib/ga.ts';
import {
  importantNotificationCategories,
  notificationCategories,
} from '#src/lib/notifications.ts';

import type {
  SignInMutation,
  SignInMutationVariables,
} from './__generated__/useSignIn.graphql.ts';
import { currentUser as currentUserFragment } from './queries.ts';

const SIGN_IN_MUTATION = gql`
  mutation SignInMutation(
    $input: signInInput!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
    $notificationCategories: [NotificationCategoryInput!]
    $importantNotificationCategories: [NotificationCategoryInput!]
  ) {
    signIn(input: $input) {
      currentUser {
        slug
        timeLeftForConfirmation
        ...CurrentUserProvider_currentUser
      }
      otpSessionChallenge
      errors {
        path
        message
        code
      }
      tcuToken
    }
  }
  ${currentUserFragment}
` as TypedDocumentNode<SignInMutation, SignInMutationVariables>;

export const useSignInMutation = () => {
  const { sport } = useSportContext();
  const { showNotification } = useSnackNotificationContext();
  const track = useEvents();
  const { setShowRestrictedAccess } = useRestrictedAccessContext();
  const [mutate] = useMutation(SIGN_IN_MUTATION);
  const { deviceFingerprint: getDeviceFingerPrint } =
    useDeviceFingerprintContext();
  const { updateQuery } = useConfigContext();

  /** necessary callback to avoid infinite loop because signIn is used on dependency arrays */
  return useCallback(
    async (props: SignInMutationVariables['input']) => {
      const deviceFingerprint = await getDeviceFingerPrint();
      const { data } = await mutate({
        variables: {
          sport,
          input: {
            ...props,
            gaClientId: getClientId(),
            deviceFingerprint,
          },
          notificationCategories,
          importantNotificationCategories,
        },
      });

      const currentUser = data?.signIn?.currentUser;

      if (currentUser) {
        if (!currentUser.confirmed) {
          const remainingHours = secondsToHours(
            Math.max(currentUser.timeLeftForConfirmation || 0, 0)
          );
          showNotification(
            'confirmEmail',
            {
              strong: Bold,
              remainingHours,
              link: createResendEmailConfirmationButton({
                onClick: () => setShowRestrictedAccess('email'),
              }),
            },
            { autoHideDuration: 30000 }
          );
          track('View Email Confirmation Reminder Toast');
        }
        updateQuery(currentUser);
        /** Redirection happens on RedirectAfterSignin */
      }
      return data?.signIn;
    },
    [
      getDeviceFingerPrint,
      mutate,
      setShowRestrictedAccess,
      showNotification,
      sport,
      track,
      updateQuery,
    ]
  );
};
